import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

const STORAGE_PREFIX = "verbleif.table.hidden-columns.";

function readStored(storageKey: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    // Private windows and blocked storage throw; the table then starts with every column shown.
    return null;
  }
}

function writeStored(storageKey: string, hidden: string[]) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(hidden));
  } catch {
    // Storage is a convenience: when it is blocked the choice lasts until the page reloads.
  }
}

/**
 * Which columns of a table are hidden. The choice lives in `hidden` (a v-model of the table) and, with a
 * `storageKey`, is remembered per browser so the list opens with the same columns next time.
 */
export function useColumnVisibility(options: {
  hidden: Ref<string[]>
  columns: Readonly<Ref<string[]>>
  storageKey: Readonly<Ref<string | null | undefined>>
}) {
  const { hidden, columns, storageKey } = options;
  const restoredFor = ref<string | null>(null);

  watch(storageKey, (key) => {
    if (!key || restoredFor.value === key) {
      return;
    }
    restoredFor.value = key;
    const stored = readStored(key);
    if (stored) {
      hidden.value = stored;
    }
  }, { immediate: true });

  watch(hidden, (value) => {
    const key = storageKey.value;
    if (!key) {
      return;
    }
    writeStored(key, value);
  }, { deep: true });

  /** Columns that are shown. At least one column always stays, so the table never ends up without any. */
  const visibleColumns = computed(() => {
    const shown = columns.value.filter(column => !hidden.value.includes(column));
    if (shown.length > 0) {
      return shown;
    }
    return columns.value.slice(0, 1);
  });

  function isVisible(column: string): boolean {
    return visibleColumns.value.includes(column);
  }

  function setVisible(column: string, visible: boolean) {
    if (visible) {
      hidden.value = hidden.value.filter(entry => entry !== column);
      return;
    }
    if (hidden.value.includes(column)) {
      return;
    }
    // Hiding the last shown column is not possible: the menu keeps that checkbox on.
    if (visibleColumns.value.length <= 1 && visibleColumns.value.includes(column)) {
      return;
    }
    hidden.value = [...hidden.value, column];
  }

  function showAll() {
    hidden.value = [];
  }

  return { visibleColumns, isVisible, setVisible, showAll };
}
